import { AnalyticsEventName, EventProperties, validateProperties } from "./analyticsEvents";

/**
 * Core Plausible tracking client.
 *
 * Handles:
 * - Environment detection (dev vs prod)
 * - Property validation
 * - Delegating to window.plausible
 */

declare global {
    interface Window {
        plausible?: (event: string, options?: { props?: Record<string, any> }) => void;
        debugAnalytics?: () => void;
    }
}

/**
 * Check if Plausible is available and ready.
 */
function isPlausibleAvailable(): boolean {
    return typeof window !== "undefined" && typeof window.plausible === "function";
}

/**
 * Track an analytics event.
 *
 * @param event - Event name (use AnalyticsEvents constants)
 * @param props - Optional event properties (use StandardProps for common keys)
 *
 * @example
 * // Basic event
 * trackEvent(AnalyticsEvents.Signup.Completed);
 *
 * // Event with properties
 * trackEvent(AnalyticsEvents.Verification.Verified, {
 *   [StandardProps.TIME_TO_VERIFY]: '5m'
 * });
 *
 * // Event with conversion data
 * trackEvent(AnalyticsEvents.Conversion.FirstEventPublished, {
 *   [StandardProps.DAYS_SINCE_SIGNUP]: 2
 * });
 */
export function trackEvent(event: AnalyticsEventName, props?: EventProperties): void {
    // Validate properties if provided
    if (props && !validateProperties(props)) {
        console.error("[Analytics] Event not tracked due to invalid properties:", event);
        return;
    }

    if (isPlausibleAvailable()) {
        if (props) {
            window.plausible!(event, { props });
        } else {
            window.plausible!(event);
        }
    } else {
        console.warn("[Analytics] Plausible not available, event not tracked:", event, props);
    }
}

/**
 * Track a pageview (handled automatically by Plausible script, but can be used for SPA navigation).
 */
export function trackPageview(): void {
    if (isPlausibleAvailable()) {
        window.plausible!("pageview");
    }
}

/**
 * Debug helper to check analytics setup.
 * Available in browser console via window.debugAnalytics()
 */
export function debugAnalytics(): void {
    // eslint-disable-next-line
    console.log("=== Analytics Debug Info ===");
    // eslint-disable-next-line
    console.log("Plausible available:", isPlausibleAvailable());
    // eslint-disable-next-line
    console.log("window.plausible:", typeof window.plausible);

    if (isPlausibleAvailable()) {
        // eslint-disable-next-line
        console.log("✅ Plausible is ready");
        // eslint-disable-next-line
        console.log("Sending test event...");
        trackEvent("debug_test" as AnalyticsEventName, {
            test_property: "test_value",
            timestamp: new Date().toISOString(),
        });
        // eslint-disable-next-line
        console.log("✅ Test event sent (check browser console or Plausible dashboard)");
    } else {
        // eslint-disable-next-line
        console.log("❌ Plausible not available");
        // eslint-disable-next-line
        console.log("Expected: In production, plausible script should be loaded from google.html.twig");
        // eslint-disable-next-line
        console.log("Expected: In development, plausible logs to console (check google.html.twig)");
    }
    // eslint-disable-next-line
    console.log("========================");
}

// Make debug helper available globally
if (typeof window !== "undefined") {
    window.debugAnalytics = debugAnalytics;
}
